import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { format } from "date-fns";

import { supportGetUserClientVersion } from "src/apiService/modules/support";
import type { UserClientVersion } from "src/interfaces/clientVersionStats.interface";
import { setSupportLoginToken } from "src/store/support/actions";
import { supportGetToken } from "src/store/support/selector";

const useStyles = makeStyles((theme) => ({
  section: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  row: {
    marginTop: theme.spacing(0.5),
  },
}));

interface ClientVersionSectionProps {
  userId: string;
}

function ClientVersionSection({ userId }: ClientVersionSectionProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState<
    UserClientVersion | null | undefined
  >();

  useEffect(() => {
    if (!token || !userId) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const { data } = await supportGetUserClientVersion(token, userId);
        if (!cancelled) setVersion(data.version);
      } catch (err: any) {
        if (err?.code === "failed-precondition") {
          dispatch(setSupportLoginToken());
        }
        if (!cancelled) setVersion(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [dispatch, token, userId]);

  return (
    <Box className={classes.section}>
      <Typography variant="h6" gutterBottom>
        Client version
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : version === undefined ? null : version === null ? (
        <Typography color="textSecondary" className={classes.row}>
          No version-check events recorded for this user.
        </Typography>
      ) : (
        <>
          <Typography className={classes.row}>
            <strong>Version:</strong> {version.clientVersion}
          </Typography>
          <Typography className={classes.row} color="textSecondary">
            <strong>Last checked:</strong>{" "}
            {version.date
              ? format(new Date(version.date), "MMM d, yyyy h:mm a")
              : "—"}
          </Typography>
        </>
      )}
    </Box>
  );
}

export default ClientVersionSection;
